import React, { Component } from 'react';
import throttle from 'lodash.throttle';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { actions as buildsActions } from '@saucelabs/sl-module-builds';
import { records as userRecords } from '@saucelabs/sl-module-user';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';
import { is } from 'immutable';
import { TitleRow } from '@saucelabs/sl-component-breadcrumbs';

import { constants as sharedConstants } from './../ducks/shared';
import { actions, selectors, utils } from './../ducks/flatBuilds';
import {
  constants as filterConstants,
  functions as filterFunctions,
  operations as filterOperations,
} from './../ducks/filters';
import AutomatedBuildsTab from './../components/AutomatedBuildsTab';
import Styles from './FlatBuildsContainer.styles';

class FlatBuildsContainer extends Component {
  constructor(props) {
    super(props);
    this.pollers = [];
    this.throttledFetchData = throttle(this.fetchData, sharedConstants.API_FETCH_THROTTLE);

    const ownerFromQS = qs.parse(props.location.search, { ignoreQueryPrefix: true }).owner_filter;
    props.initializeFilters({
      username: props.username,
      ownerFilter: ownerFromQS ?
        {
          type: props.filterType,
          data: { name: ownerFromQS, label: ownerFromQS, type: 'user' },
        } :
        undefined,
      fetchUsers: props.shouldFetchOrgUsers,
      fetchTeams: props.jobsTeamSharingFeatureEnabled,
    });

    const status = props.filters.getIn([
      props.filterType,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: props.filters,
      filterType: props.filterType,
      isSubaccountDashboard: props.isSubaccountDashboard,
      isOpenSauceUser: props.isOpenSauceUser,
      username: props.username,
    });
    this.fetchData({
      ownership,
      statusList: status,
      tabName: props.filterType,
    });
  }

  componentDidUpdate(prevProps) {
    const { filters: oldFilters } = prevProps;
    const {
      filters,
      // we dont have to use previous values of those, since they won't change in filters scope
      filterType,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    } = this.props;

    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: oldFilters,
      filterType,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const nextOwnership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const status = oldFilters.getIn([filterType, filterConstants.FILTER_TYPE_STATUS]);
    const nextStatusList = filters.getIn([filterType, filterConstants.FILTER_TYPE_STATUS]);

    const anyFilterChanged = (
      !is(status, nextStatusList) || (ownership?.name !== nextOwnership.name)
    );

    if (anyFilterChanged) {
      this.throttledFetchData({
        ownership: nextOwnership,
        statusList: nextStatusList,
        tabName: filterType,
      });
    }
  }

  componentWillUnmount() {
    const { cleanup } = this.props;
    this.pollers.forEach(clearInterval);
    cleanup();
  }

  toggleStatusFilter = (status) => {
    const { trackedToggleStatus } = this.props;
    trackedToggleStatus(status, { type: 'build' });
  };

  callAPIWithIntervals(apiCalls) {
    this.pollers.forEach(clearInterval);
    apiCalls.forEach((apiCall) => apiCall());
    this.pollers = apiCalls.map((apiCall) => (
      setInterval(() => apiCall(), sharedConstants.API_FETCH_INTERVAL)
    ));
  }

  fetchData({ ownership: argOwnership, statusList }) {
    const {
      getAnnotatedBuilds,
      getBuilds,
      isSubaccountDashboard,
      isOpenSauceUser,
      filters,
      filterType,
      jobsTeamSharingFeatureEnabled,
      organizationId,
      username,
    } = this.props;
    const propsOwnership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const ownership = argOwnership || propsOwnership;
    // fetch tab specific data when tab is activated
    const pollers = [];
    const params = utils.buildQueryParams({
      jobsTeamSharingFeatureEnabled, organizationId, ownership, username, statusList,
    });

    pollers.push(() => (
      jobsTeamSharingFeatureEnabled ? getAnnotatedBuilds(params) : getBuilds(params)
    ));

    this.callAPIWithIntervals(pollers);
  }

  render() {
    const {
      buildsLoading,
      breadcrumbs,
      clearStatuses,
      filters,
      filtersInitialized,
      filterType,
      isOpenSauceUser,
      isSubaccountDashboard,
      jobsTeamSharingEnabled,
      jobsTeamSharingFeatureEnabled,
      orgTeams,
      orgUsers,
      selectOwner,
      pageTitle,
      toggleStatus,
      user,
      username,
    } = this.props;
    const status = filters.getIn([filterType, filterConstants.FILTER_TYPE_STATUS]);
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const owners = filterFunctions.buildOwnerFilter({
      isSubaccountDashboard,
      jobsTeamSharingFeatureEnabled,
      jobsTeamSharingEnabled,
      orgTeams,
      orgUsers,
      user,
      username,
    });
    const showSeeAllButton = isSupportedFeature(features.ARCHIVES) && !isOpenSauceUser;
    const noFilters = filterFunctions.noFilterInUse({
      filters,
      filterType,
      ownership,
      username,
    });
    return (
      <div className={Styles.wrapper}>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <TitleRow list={breadcrumbs} />
        <AutomatedBuildsTab
          buildsLoading={buildsLoading}
          clearStatuses={(payload) => clearStatuses({
            type: filterType,
            filter: payload,
          })}
          filtersInitialized={filtersInitialized}
          isOpenSauceUser={isOpenSauceUser}
          noFilters={noFilters}
          owners={owners}
          ownership={ownership}
          selectOwner={(payload) => selectOwner({
            type: filterType,
            data: payload,
          })}
          showOwnersFilter={!isOpenSauceUser}
          showSeeAllButton={showSeeAllButton}
          status={status}
          toggleStatus={toggleStatus}
          trackedToggleStatus={(payload) => this.toggleStatusFilter({
            type: filterType,
            data: payload,
          })}
          user={user}
          username={username}
        />
      </div>
    );
  }
}

FlatBuildsContainer.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string }),
  ).isRequired,
  buildsLoading: PropTypes.bool.isRequired,
  cleanup: PropTypes.func.isRequired,
  clearStatuses: PropTypes.func.isRequired,
  filters: ImmutablePropTypes.map.isRequired,
  filtersInitialized: PropTypes.bool.isRequired,
  filterType: PropTypes.oneOf([
    filterConstants.FILTER_AUTOMATED_BUILDS,
    filterConstants.FILTER_HEADLESS_BUILDS,
  ]).isRequired,
  getAnnotatedBuilds: PropTypes.func.isRequired,
  getBuilds: PropTypes.func.isRequired,
  initializeFilters: PropTypes.func.isRequired,
  isOpenSauceUser: PropTypes.bool,
  isSubaccountDashboard: PropTypes.bool,
  jobsTeamSharingEnabled: PropTypes.bool.isRequired,
  jobsTeamSharingFeatureEnabled: PropTypes.bool.isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orgTeams: ImmutablePropTypes.list.isRequired,
  orgUsers: ImmutablePropTypes.list.isRequired,
  pageTitle: PropTypes.string.isRequired,
  selectOwner: PropTypes.func.isRequired,
  shouldFetchOrgUsers: PropTypes.bool.isRequired,
  toggleStatus: PropTypes.func.isRequired,
  trackedToggleStatus: PropTypes.func.isRequired,
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
  username: PropTypes.string.isRequired,
};

FlatBuildsContainer.defaultProps = {
  isOpenSauceUser: false,
  isSubaccountDashboard: false,
  organizationId: null,
};

const mapStateToProps = selectors.containerSelector;
const mapDispatchToProps = {
  cleanup: actions.cleanup,
  clearStatuses: filterOperations.clearStatuses,
  getBuilds: buildsActions.getBuilds,
  getAnnotatedBuilds: buildsActions.getAnnotatedBuilds,
  initializeFilters: filterOperations.initializeFilters,
  selectOwner: filterOperations.selectOwner,
  toggleStatus: filterOperations.toggleStatus,
  trackedToggleStatus: filterOperations.trackedToggleJobFilter,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(FlatBuildsContainer),
);
export { FlatBuildsContainer };
